import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import SignaturePad from "@/components/ResponsiveSignaturePad";
import { getCurrentLocation } from "@/lib/geolocation";
import {
    ArrowLeft, MapPin, Map as MapIcon, Truck, ChevronRight, UserCog, Briefcase,
    ClipboardCheck, Play, MessageSquarePlus, StickyNote, Phone, CheckCircle2,
} from "lucide-react";

export default function AuditServiceDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const [service, setService] = useState(null);
    const [client, setClient] = useState(null);
    const [hasMaps, setHasMaps] = useState(false);
    const [hasVehicles, setHasVehicles] = useState(false);
    const [clientNotes, setClientNotes] = useState([]);
    const [auditorNotes, setAuditorNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    const [showClientNote, setShowClientNote] = useState(false);
    const [cnText, setCnText] = useState("");
    const [cnBy, setCnBy] = useState("");
    const cnSig = useRef(null);

    const [anText, setAnText] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const { data: s } = await api.get(`/schedules/${id}`);
            setService(s);
            const [cRes, fpRes, vRes, cnRes, anRes] = await Promise.all([
                api.get("/clients"),
                api.get(`/floor-plans?client_id=${s.client_id}`).catch(() => ({ data: [] })),
                api.get(`/transport/vehicles?client_id=${s.client_id}`).catch(() => ({ data: [] })),
                api.get(`/schedules/${id}/client-notes`).catch(() => ({ data: [] })),
                api.get(`/schedules/${id}/tech-notes`).catch(() => ({ data: [] })),
            ]);
            setClient((cRes.data || []).find((x) => x.id === s.client_id) || null);
            setHasMaps((fpRes.data || []).length > 0);
            setHasVehicles((vRes.data || []).length > 0);
            setClientNotes(cnRes.data || []);
            setAuditorNotes(anRes.data || []);
        } catch {
            toast.error("No se pudo cargar la auditoría");
        } finally { setLoading(false); }
    };
    // eslint-disable-next-line
    useEffect(() => { load(); }, [id]);

    const handleStart = async () => {
        setBusy(true);
        try {
            if (service?.entry_time || service?.status === "in_progress") {
                nav(`/audit/service/${id}/flow`);
                return;
            }
            toast.info("Obteniendo ubicación...");
            const geo = await getCurrentLocation({ timeoutMs: 12000, enableHighAccuracy: true });
            const payload = geo.ok ? { latitude: geo.latitude, longitude: geo.longitude, accuracy_m: geo.accuracy_m } : {};
            await api.post(`/schedules/${id}/start`, payload);
            toast.success(geo.ok ? "Auditoría iniciada" : "Iniciada — sin ubicación");
            nav(`/audit/service/${id}/flow`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al iniciar");
        } finally { setBusy(false); }
    };

    const saveClientNote = async () => {
        if (!cnText.trim()) return toast.error("Escriba la nota");
        setBusy(true);
        try {
            let sig = null;
            if (cnSig.current && !cnSig.current.isEmpty()) sig = cnSig.current.toDataURL("image/png");
            const geo = await getCurrentLocation({ timeoutMs: 8000 }).catch(() => ({ ok: false }));
            await api.post(`/schedules/${id}/client-notes`, {
                scheduled_service_id: id,
                notes: cnText.trim(),
                accepted_by_name: cnBy.trim() || null,
                client_signature_base64: sig,
                latitude: geo.ok ? geo.latitude : null,
                longitude: geo.ok ? geo.longitude : null,
                accuracy_m: geo.ok ? geo.accuracy_m : null,
            });
            toast.success("Nota del cliente guardada");
            setShowClientNote(false); setCnText(""); setCnBy("");
            const anRes = await api.get(`/schedules/${id}/client-notes`);
            setClientNotes(anRes.data || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo guardar");
        } finally { setBusy(false); }
    };

    const saveAuditorNote = async () => {
        if (!anText.trim()) return;
        setBusy(true);
        try {
            await api.post(`/schedules/${id}/tech-notes`, { scheduled_service_id: id, notes: anText.trim() });
            setAnText("");
            const r = await api.get(`/schedules/${id}/tech-notes`);
            setAuditorNotes(r.data || []);
            toast.success("Nota del auditor guardada");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo guardar");
        } finally { setBusy(false); }
    };

    if (loading) return <div className="p-4 space-y-3">{[1, 2, 3].map(i => <div key={i} className="h-20 rounded-xl bg-secondary animate-pulse" />)}</div>;
    if (!service) return <div className="p-4 text-sm text-muted-foreground">Auditoría no encontrada.</div>;

    const salesperson = service.salesperson || client?.salesperson;
    const isCompleted = service.status === "completed";

    return (
        <div className="p-4" data-testid="audit-detail-page">
            <Link to="/audit" className="inline-flex items-center text-sm text-muted-foreground mb-3 hover:text-foreground" data-testid="audit-back">
                <ArrowLeft className="w-4 h-4 mr-1" /> Mi ruta
            </Link>

            {/* Header */}
            <Card className="p-4 mb-3">
                <div className="flex items-center gap-2 mb-1">
                    <ClipboardCheck className="w-4 h-4 text-emerald-600" />
                    <span className="text-[11px] uppercase tracking-wider font-semibold text-emerald-700">Auditoría de calidad</span>
                </div>
                <h1 className="text-lg font-semibold leading-tight">{client?.name || "Cliente"}</h1>
                {client?.address && (
                    <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                        <MapPin className="w-3 h-3" /> {client.address}
                    </div>
                )}
                <div className="text-xs text-muted-foreground mt-1">{service.scheduled_date} · {service.scheduled_time}</div>
            </Card>

            {/* Nota del cliente */}
            <Card className="p-4 mb-3" data-testid="audit-client-note-section">
                <div className="flex items-center justify-between mb-2">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Nota del cliente</div>
                    <Button size="sm" variant="outline" onClick={() => setShowClientNote(true)} data-testid="add-client-note-btn">
                        <MessageSquarePlus className="w-4 h-4 mr-1" /> Agregar
                    </Button>
                </div>
                {clientNotes.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Sin notas del cliente.</p>
                ) : (
                    <div className="space-y-2">
                        {clientNotes.map((n) => (
                            <div key={n.id} className="text-sm bg-secondary rounded-lg p-2">
                                <div>{n.notes}</div>
                                {n.client_name && <div className="text-[11px] text-muted-foreground mt-1">— {n.client_name}</div>}
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            {/* Mapa interactivo */}
            {hasMaps && (
                <Card className="p-4 mb-3 cursor-pointer hover:shadow-md" onClick={() => nav(`/audit/service/${id}/map`)} data-testid="audit-map-card">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <MapIcon className="w-4 h-4 text-[hsl(var(--trulyn-navy))]" />
                            <span className="font-semibold text-sm">Mapa interactivo</span>
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                </Card>
            )}

            {/* Control de transportes */}
            {hasVehicles && (
                <Card className="p-4 mb-3 cursor-pointer hover:shadow-md" onClick={() => nav(`/audit/service/${id}/transport`)} data-testid="audit-transport-card">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Truck className="w-4 h-4 text-[hsl(var(--trulyn-navy))]" />
                            <span className="font-semibold text-sm">Control de Transportes</span>
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                </Card>
            )}

            {/* Personas involucradas */}
            <Card className="p-4 mb-3">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Personas involucradas</div>
                <div className="space-y-2">
                    <InfoRow icon={UserCog} label="Técnico anterior" value={service.previous_technician} />
                    <InfoRow icon={Briefcase} label="Vendedor" value={salesperson} phone={service.salesperson_phone} />
                </div>
            </Card>

            {/* Notas del Auditor */}
            <Card className="p-4 mb-3" data-testid="audit-notes-section">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1">
                    <StickyNote className="w-3.5 h-3.5" /> Notas del Auditor
                </div>
                {auditorNotes.length > 0 && (
                    <div className="space-y-2 mb-2">
                        {auditorNotes.map((n) => (
                            <div key={n.id} className="text-sm bg-secondary rounded-lg p-2">{n.notes}</div>
                        ))}
                    </div>
                )}
                <Textarea value={anText} onChange={(e) => setAnText(e.target.value)} placeholder="Escriba una nota interna..." className="mb-2" data-testid="auditor-note-input" />
                <Button size="sm" onClick={saveAuditorNote} disabled={busy || !anText.trim()} data-testid="save-auditor-note-btn">Guardar nota</Button>
            </Card>

            {/* Start / Continue */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-3 z-20">
                <div className="max-w-2xl mx-auto">
                    <Button
                        className="w-full h-12 text-base font-semibold bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={handleStart}
                        disabled={busy}
                        data-testid="start-audit-btn"
                    >
                        {isCompleted ? (<><CheckCircle2 className="w-5 h-5 mr-2" /> Ver auditoría</>)
                            : service.status === "in_progress" ? (<><Play className="w-5 h-5 mr-2" /> Continuar auditoría</>)
                                : (<><Play className="w-5 h-5 mr-2" /> Iniciar auditoría</>)}
                    </Button>
                </div>
            </div>

            {/* Client note dialog */}
            <Dialog open={showClientNote} onOpenChange={(o) => { if (!o) setShowClientNote(false); }}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle>Nota del cliente</DialogTitle>
                        <DialogDescription>El cliente puede dejar una observación y firmar.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <Textarea value={cnText} onChange={(e) => setCnText(e.target.value)} placeholder="Observación del cliente..." data-testid="client-note-text" />
                        <Input value={cnBy} onChange={(e) => setCnBy(e.target.value)} placeholder="Nombre de quien firma (opcional)" data-testid="client-note-by" />
                        <div>
                            <div className="text-xs text-muted-foreground mb-1">Firma (opcional)</div>
                            <div className="border rounded-lg overflow-hidden">
                                <SignaturePad ref={cnSig} height={140} />
                            </div>
                            <button className="text-xs text-muted-foreground mt-1" onClick={() => cnSig.current?.clear()}>Limpiar firma</button>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowClientNote(false)}>Cancelar</Button>
                        <Button onClick={saveClientNote} disabled={busy} data-testid="save-client-note-btn">Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function InfoRow({ icon: Icon, label, value, phone }) {
    return (
        <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-[#FFD62B]/20 flex items-center justify-center shrink-0">
                <Icon className="w-3.5 h-3.5 text-[hsl(var(--trulyn-navy))]" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-[11px] text-muted-foreground">{label}</div>
                <div className="text-sm font-medium truncate">{value || "—"}</div>
            </div>
            {phone && (
                <a href={`tel:${phone}`} className="text-emerald-600" title="Llamar"><Phone className="w-4 h-4" /></a>
            )}
        </div>
    );
}
